import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import landImage from '../../cheese-journey-land.webp';
import milkImage from '../../cheese-journey-milk.webp';
import makeImage from '../../cheese-journey-make.webp';
import waitImage from '../../cheese-journey-wait.webp';
import tasteImage from '../../cheese-journey-taste.webp';
import routeImage from '../../cheese-journey-route.webp';
import './CheeseJourneyPage.css';
import './CheeseJourneyV5.css';

const chapters = [
  { id:'land', num:'01', kicker:'Where it all begins', title:'The Land', body:'Rolling pastures, clean water, and four distinct seasons shape the milk—and the cheese.', proof:'Place changes flavor', chips:['Pasture','Climate','Water'], image:landImage },
  { id:'milk', num:'02', kicker:'Nature’s perfect starting point', title:'The Milk', body:'Cow, goat, or sheep—each milk brings its own character, influenced by breed, feed, season, and farm.', proof:'Milk is already a decision', chips:['Cow','Goat','Sheep'], image:milkImage },
  { id:'make', num:'03', kicker:'Craft, culture, and technique', title:'The Make', body:'Cultures, rennet, heat, and expert hands transform milk into curd—then into something extraordinary.', proof:'Technique becomes character', chips:['Cultures','Rennet','Curd'], image:makeImage },
  { id:'wait', num:'04', kicker:'Time creates extraordinary', title:'The Wait', body:'In cool, quiet caves, wheels are washed, turned, and cared for—developing flavor, texture, and soul.', proof:'Aging is active work', chips:['Humidity','Rind','Time'], image:waitImage },
  { id:'taste', num:'05', kicker:'A world of flavor and tradition', title:'The Taste', body:'From alpine-style to washed rind, each cheese tells a story of place, people, and heritage.', proof:'Taste the map', chips:['Alpine','Washed rind','Fresh'], image:tasteImage },
  { id:'route', num:'06', kicker:'Plan your own cheese journey', title:'Your Route', body:'Explore trail hubs, visit cheeseries, find great stays, and build an unforgettable New England adventure.', proof:'Map less. Taste more', chips:['Trail hubs','Stays','Itinerary'], image:routeImage },
] as const;

const clamp = (value:number, min=0, max=1) => Math.min(max, Math.max(min, value));

export default function CheeseJourneyPage() {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const ids = useMemo(() => chapters.map(c => c.id), []);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const journeyRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'The Journey of New England Cheese | Curd Compass';
    let raf = 0;

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        setProgress(clamp(window.scrollY / max));
        const distances = ids.map(id => {
          const el = document.getElementById(id);
          if (!el) return Number.POSITIVE_INFINITY;
          const rect = el.getBoundingClientRect();
          return Math.abs(rect.top + rect.height / 2 - window.innerHeight / 2);
        });
        setActive(distances.indexOf(Math.min(...distances)));

        const journey = journeyRef.current;
        const video = videoRef.current;
        if (videoReady && journey && video?.duration) {
          const rect = journey.getBoundingClientRect();
          const scrollable = Math.max(1, journey.offsetHeight - window.innerHeight);
          const localProgress = clamp(-rect.top / scrollable);
          const target = localProgress * Math.max(0, video.duration - 0.05);
          if (Math.abs(video.currentTime - target) > 0.03) video.currentTime = target;
        }
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive:true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      document.title = previousTitle;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [ids, videoReady]);

  const current = chapters[active];

  return (
    <div className="cj-page">
      <div className="cj-progress"><i style={{ transform:`scaleX(${progress})` }} /></div>
      <header className="cj-topbar">
        <Link to="/" className="cj-brand"><span>✦</span>Curd Compass</Link>
        <nav><Link to="/trails">Trail hubs</Link><Link to="/cheeses">Cheeseries</Link><Link to="/stays">Stays</Link><Link to="/plan">Itinerary</Link></nav>
        <Link className="cj-trip" to="/plan">Plan your trip</Link>
      </header>

      <section className="cj-hero">
        <img src={chapters[0].image} alt="New England cheese country miniature diorama" />
        <div className="cj-hero-shade" />
        <div className="cj-hero-copy">
          <div className="cj-eyebrow">A cinematic field guide</div>
          <h1>The journey of exceptional New England cheese.</h1>
          <p>From lush pastures to cave-aged perfection, discover the craft, culture, and places behind every unforgettable bite.</p>
          <div className="cj-meta"><span>Vermont</span><span>Maine</span><span>New Hampshire</span><span>6 chapters</span></div>
        </div>
      </section>

      <section className="cj-journey" ref={journeyRef}>
        <div className="cj-stage">
          <div className="cj-frame">
            <div className="cj-frame-head"><span>Curd Compass · Scroll World V5</span><span>{current.num} / 06</span></div>
            <div className={`cj-viewport ${videoReady ? 'video-ready' : ''}`}>
              <video ref={videoRef} className="cj-scrub-video" src="/curdcompass-cheese-journey-v5-master.mp4" poster={landImage} muted playsInline preload="auto" onLoadedMetadata={() => setVideoReady(true)} onError={() => setVideoReady(false)} aria-label="Scroll-controlled journey through six New England cheese dioramas" />
              <div className="cj-still-fallback" aria-hidden={videoReady}>
                {chapters.map((chapter,index) => (
                  <figure key={chapter.id} className={`cj-scene ${index===active?'active':''}`}>
                    <img src={chapter.image} alt={`${chapter.title} miniature diorama`} loading={index < 2 ? 'eager' : 'lazy'} />
                  </figure>
                ))}
              </div>
              <div className="cj-scene-copy">
                <div className="cj-number">{active + 1}</div>
                <strong>{current.kicker}</strong>
                <h2>{current.title}</h2>
                <p>{current.body}</p>
                <span className="cj-proof">{current.proof}</span>
              </div>
            </div>
          </div>
          <nav className="cj-routebar">
            {chapters.map((chapter,index)=><a className={index===active?'active':''} href={`#${chapter.id}`} key={chapter.id}><span>{index+1}</span><b>{chapter.title}</b></a>)}
          </nav>
        </div>

        <div className="cj-beats">
          {chapters.map(chapter => (
            <article className="cj-beat" id={chapter.id} data-num={chapter.num} key={chapter.id}>
              <small>{chapter.num} · {chapter.title}</small>
              <h3>{chapter.title === 'Your Route' ? 'Plan your own cheese journey.' : chapter.kicker}</h3>
              <p>{chapter.body}</p>
              <div className="cj-chips">{chapter.chips.map(chip=><span key={chip}>{chip}</span>)}</div>
            </article>
          ))}
        </div>
      </section>

      <section className="cj-finale">
        <img src={chapters[5].image} alt="New England cheese trail miniature route" loading="lazy" />
        <div className="cj-finale-shade" />
        <div><div className="cj-eyebrow">The world ends. Your route begins.</div><h2>Ready to plan your cheese journey?</h2><p>Find cheeseries, build your itinerary, and discover the best of Vermont, Maine, and New Hampshire.</p><div className="cj-actions"><Link className="primary" to="/trails">Explore trail hubs</Link><Link className="secondary" to="/plan">Build an itinerary</Link></div></div>
      </section>
      <footer className="cj-footer"><span>Curd Compass</span><span>Vermont · Maine · New Hampshire</span><span>Cheese Journey V5</span></footer>
    </div>
  );
}
